<template>
	<div
		class="profile-form"
		:class="{ '-read-only': isReadOnly }"
	>
		<div
			v-if="$arenaConfig.profileFields.additionalFieldsEnabled"
			class="profile-additional-fields"
		>
			<Select
				ref="languageInput"
				:label="$I18n.trans_choice('commons.language', 1)"
				:options="languageOptions"
				:value="localProfile.locale"
				:searchable="false"
				:clearable="false"
				:required="true"
				:dark-theme="!$arenaConfig.isLightTheme"
				@change="val => localProfile.locale = val"
			/>
			<Select
				v-if="countryOptions"
				ref="countryInput"
				:label="$I18n.trans('commons.country')"
				:options="countryOptions"
				:value="localProfile.country"
				:placeholder="$I18n.trans('commons.select')"
				:searchable="true"
				:clearable="false"
				:required="$arenaConfig.additionalFieldsRequired"
				:dark-theme="!$arenaConfig.isLightTheme"
				@change="val => localProfile.country = val"
			/>
			<InputCheckbox
				v-if="profileLoaded"
				id="newsletter-consent"
				v-model="localProfile.newsletter"
				required="true"
				:dark-theme="!$arenaConfig.isLightTheme"
				:label="$I18n.trans('commons.newsletter_consent')"
			/>
		</div>
		<InputText
			ref="firstNameInput"
			v-model="localProfile.firstName"
			:label="$I18n.trans('commons.first_name')"
			:dark-theme="!$arenaConfig.isLightTheme"
			:disabled="isProcessing || isReadOnly"
			autocomplete="given-name"
			:hide-optional="isReadOnly"
		/>
		<InputText
			ref="lastNameInput"
			v-model="localProfile.lastName"
			:label="$I18n.trans('commons.last_name')"
			:dark-theme="!$arenaConfig.isLightTheme"
			:disabled="isProcessing || isReadOnly"
			autocomplete="family-name"
			:hide-optional="isReadOnly"
		/>
		<InputBirthdate
			ref="birthdateInput"
			v-model="localProfile.birthday"
			:disabled="isProcessing || isReadOnly"
			:dark-theme="!$arenaConfig.isLightTheme"
			:hide-optional="isReadOnly"
		/>
	</div>
</template>

<script>
import { getAllCountryNames } from '@shared/utils/countries';

import InputText from '@shared/components/Form/InputText';
import InputBirthdate from '@shared/components/Form/InputBirthdate';
import Select from '@shared/components/Select';
import InputCheckbox from '@shared/components/Form/InputCheckbox';
import { mapState } from 'vuex';
import { actions } from '@shared/js/store/modules/user';

export default {
	name:       'ProfileForm',
	components: { InputText, InputBirthdate, Select, InputCheckbox },
	props:      {
		isReadOnly: {
			type:     Boolean,
			required: false,
			default:  false,
		},
	},
	data() {
		return {
			isProcessing: false,
			localProfile: {
				firstName:  null,
				lastName:   null,
				birthday:   null,
				country:    null,
				locale:     null,
				newsletter: false,
			},
			isMounted:      false,
			countryOptions: null,
			profileLoaded:  false,
		};
	},
	computed: {
		...mapState({
			profile: state => state.user.profile,
		}),
		formHasChanged() {
			if (!this.profile) return false;
			// check every property of localProfile against profile
			const checkKey = ['firstName', 'lastName', 'birthday', 'country', 'locale', 'newsletter'];
			return checkKey.some(key => this.localProfile[key] !== this.profile[key]);
		},
		formIsValid() {
			if (!this.isMounted) return false;
			const basicFieldsValid = this.$refs.firstNameInput?.isValid && this.$refs.lastNameInput?.isValid && this.$refs.birthdateInput?.isValid;

			if (this.$arenaConfig.profileFields && this.$arenaConfig.profileFields.additionalFieldsEnabled && this.$arenaConfig.profileFields.additionalFieldsRequired) {
				return basicFieldsValid && this.localProfile.locale != null && this.localProfile.country != null;
			} else {
				return basicFieldsValid;
			}
		},
		canSubmit() {
			return this.formHasChanged && this.formIsValid;
		},
		languageOptions() {
			return this.$arenaConfig.locales.map((lang) => {
				return {
					label: lang.name,
					value: lang.code,
				};
			});
		},
		currentLanguage() {
			return this.$arenaConfig.locales?.find((locale) => locale.code === this.$arenaConfig.currentLanguageKey)?.code;
		},
		newsletterOptions() {
			return [
				{ name: this.$I18n.trans('commons.yes'), id: true },
				{ name: this.$I18n.trans('commons.no'), id: false },
			];
		},
	},
	watch: {
		isProcessing: {
			handler(value) {
				this.$emit('isProcessing', value);
			},
		},
		canSubmit: {
			handler(value) {
				this.$emit('canSubmit', value);
			},
		},
		profile: {
			immediate: true,
			handler(profile) {
				if (profile && !profile.profileUpdated && this.$arenaConfig.profileFields.newsletterOptOut) {
					profile.newsletter = true;
				}

				this.localProfile = {
					...this.localProfile,
					...profile,
				};

				if (profile) {
					this.profileLoaded = true;
				}
			},
		},
	},
	async mounted() {
		this.countryOptions = await this.getCountryOptions();
		this.isMounted = true;
	},
	methods: {
		async handleProfileUpdate() {
			try {
				this.isProcessing = true;
				await this.$store.dispatch(`user/${actions.UPDATE_PROFILE}`, this.localProfile);
				this.$toast.success($I18n.trans('notifications.profile_updated'));

				if (this.localProfile.locale !== this.currentLanguage) {
					setTimeout(() => {
						window.location.href = window.location.href.split('?')[0] + '?locale=' + this.localProfile.locale;
					}, 200);
				}
				this.$emit('updated');
			} catch (e) {
				this.$toast.error(this.$I18n.trans('errors.500.title'));
			} finally {
				this.isProcessing = false;
			}
		},
		submit() {
			this.handleProfileUpdate();
		},
		async getCountryOptions() {
			const language = ['de', 'fr', 'it', 'es', 'ca', 'en'].includes(this.currentLanguage) ? this.currentLanguage : 'en';

			try {
				// Get all country names at once for better performance
				const countryNames = await getAllCountryNames(language);

				// Convert to the format expected by the Select component
				return Object.keys(countryNames).map(code => {
					return {
						label: countryNames[code],
						value: code.toLowerCase(),
					};
				});
			} catch (error) {
				console.error('Error loading country options:', error);
				// Return empty array as fallback
				return [];
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.profile-form {
	&.-read-only {
		pointer-events: none;
	}
}

.profile-additional-fields {
	margin-bottom: $sp6;
}

</style>
